/**
 * Reads + writes the platform master-switch flag. Admin-only callables
 * — surfaces lock state, last-modifier, and any GCP side-effect errors
 * so the UI can show a useful diagnostic banner.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';

export interface PlatformRuntimeState {
  enabled: boolean;
  lastChangedAt?: number | null;
  lastChangedBy?: string | null;
  lastChangedEmail?: string | null;
  lastError?: string | null;
}

const QK = ['platform-runtime'] as const;

export function usePlatformRuntime() {
  const { user, isAdmin } = useUser();
  return useQuery<PlatformRuntimeState>({
    queryKey: QK,
    enabled: !!user && isAdmin,
    staleTime: 10_000,
    queryFn: async () => {
      const fn = httpsCallable<unknown, PlatformRuntimeState>(functions, 'getPlatformRuntime');
      const r = await fn({});
      return r.data;
    },
  });
}

export function useSetPlatformRuntime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const fn = httpsCallable<
        { enabled: boolean },
        { ok: true; enabled: boolean; warnings: string[] }
      >(functions, 'setPlatformRuntime');
      const r = await fn({ enabled });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}
